import { useEffect, useRef, useState } from 'react'
import type { JSX } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { Button, Loader } from '@buffer-mono/popcorn'

import {
  preloadComposerDialog,
  usePostComposer,
} from '~publish/hooks/usePostComposer'
import type { PrefillPostData } from '~publish/hooks/usePostComposer'
import { SEGMENT_NAMES } from '~publish/legacy/constants'
import { useAppSelector } from '~publish/legacy/store'

import formatPostFromQuery from '../ComposePage/formatPostFromQuery'

import styles from './EmbeddedComposePage.module.css'

// Subframe navigations (loading a cross-origin URL into an <iframe>) never
// count as "top-level" for SameSite=Lax cookies, so a direct visit here would
// never carry a session anyway. window.top comparisons never throw
// cross-origin (unlike reading properties on the other window), so no
// same-origin guard is needed here.
const isEmbeddedInFrame =
  typeof window !== 'undefined' && window.self !== window.top

/**
 * EmbeddedComposePage is used when the composer is loaded inside an iframe
 * (e.g. from the browser extension's embedded widget). GraphQL requests are
 * proxied through the same origin (/graphql) so cookies are sent as
 * first-party. When the composer closes, a postMessage notifies the parent
 * frame so the extension can hide the widget.
 */
const EmbeddedComposePage = (): JSX.Element => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [composerWasOpened, setComposerWasOpened] = useState<boolean>(false)
  const hasOpenedComposerRef = useRef(false)
  const { isOpen, createNewPostInComposer } = usePostComposer()
  const orgsLoaded = useAppSelector((state) => state.organizations.loaded)
  const profilesLoaded = useAppSelector((state) => state.profileSidebar.loaded)
  const hasCurrentOrg = !!useAppSelector(
    (state) => state.organizations?.selected,
  )

  const isLoading = !orgsLoaded || !profilesLoaded || !hasCurrentOrg

  // This route always opens the composer, so preload its chunk in parallel
  // with the data load below instead of waiting on it.
  useEffect(() => {
    if (isEmbeddedInFrame) preloadComposerDialog()
  }, [])

  // Open composer once data is loaded
  useEffect(() => {
    if (!isEmbeddedInFrame || isLoading || hasOpenedComposerRef.current) return

    hasOpenedComposerRef.current = true
    const rawQuery = searchParams.toString()
    const queryParams = rawQuery ? `?${rawQuery}` : ''
    const prefillPostData: PrefillPostData = formatPostFromQuery(queryParams)

    void createNewPostInComposer({
      prefillPostData,
      channels: [],
      cta: SEGMENT_NAMES.EXTENSION_CREATE_A_POST,
    })
    // We only want to open the composer once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, searchParams])

  useEffect(() => {
    if (!isEmbeddedInFrame) return

    if (isOpen) {
      setComposerWasOpened(true)
    }

    if (!isOpen && composerWasOpened) {
      setComposerWasOpened(false)
      window.parent.postMessage({ type: 'buffer|close' }, '*')
    }
  }, [isOpen, composerWasOpened])

  // Notify the parent that the app is ready
  useEffect(() => {
    if (isEmbeddedInFrame && !isLoading) {
      window.parent.postMessage({ type: 'buffer|ready' }, '*')
    }
  }, [isLoading])

  if (!isEmbeddedInFrame) {
    return (
      <div className={styles.container}>
        <div className={styles.notice}>
          <p>This page only works inside the Buffer browser extension.</p>
          <Button variant="primary" onClick={() => navigate('/')}>
            Go to Buffer
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      {isLoading && (
        <div className={styles.loader}>
          <Loader />
        </div>
      )}
    </div>
  )
}

export default EmbeddedComposePage
